/**
 * ENG-7612 (ADR-0044, P2): the typed watch-kind for a `status=waiting` card.
 *
 * P1 gave a waiting card one free-text `waiting_on` string. This adds a machine
 * -typed shape - `waiting_kind` + a `waiting_context` param bag - so the UI can
 * render a proper action button DERIVED from structure ("Review PR #3242"), and
 * so the P2 durable auto-return resolver knows what to watch and how to check
 * it. Both derive from ONE source of truth (the context), so there is no stored
 * URL to drift.
 *
 * Pure, framework-free helpers so the webapp render path and the (future) API
 * resolver share the exact same derivation.
 */

/**
 * ENG-8810: the status a parked card returns to when its wait resolves.
 *
 * `todo`, not `in_progress`, for two reasons.
 *
 * The correctness one: `lease_expires_at` is stamped when a card enters
 * `in_progress` and is meaningless in any other status, but the un-park path
 * did not clear it - so a card parked longer than the lease TTL (default 1h)
 * came back to `in_progress` carrying a lease that had already expired, often
 * hours earlier. The stale-item reaper selects exactly
 * `status='in_progress' AND lease_expires_at < now()` and runs every 10
 * minutes, so answering an overnight card started a race between the agent's
 * first write and the next sweep - and the sweep usually won, dead-lettering
 * the card and paging its owner with "nobody is working it". The longer a human
 * took to reply, the more likely their reply was thrown away. Returning to
 * `todo` removes the race at the root rather than outrunning it, because the
 * sweep never looks at `todo`.
 *
 * The honesty one (Brad, off a live 3-concurrent incident): `in_progress` is a
 * claim that the agent is working the card RIGHT NOW. At the moment a human
 * answers, that is not true - the agent may be mid-turn on something else, and
 * a revived card should not jump the queue. `todo` says what is actually true:
 * this is ready to be picked up. The agent is still notified immediately over
 * the direct-chat rail; its first act is to pull the card, which stamps a fresh
 * lease that means something.
 *
 * Rejected alternative: stamp a new lease on un-park. It stops the reap but
 * keeps the card asserting active work that nobody has started - the same
 * dishonest board state, from the other direction.
 *
 * Exported as a constant rather than written inline at each call site because
 * the un-park status is a CONTRACT: the GitHub webhook's compensating re-park
 * guards on it, and the console's optimistic update mirrors it. A literal in
 * three files is how those silently drift apart.
 */
export const UNPARKED_CARD_STATUS = 'todo' as const;

/**
 * The class of thing a parked card is waiting on.
 *  - `pr-review`  parked until a PR is reviewed (CodeRabbit / a human reviewer)
 *  - `pr-merged`  parked until a PR is merged
 *  - `human`      parked on a person's decision (ball in a human's court)
 *  - `external`   parked on a third party / system (no human to nudge)
 *  - `other`      the generic catch-all when none of the above fit (or the
 *                 classifier is unsure); renders the free-text `waiting_on`.
 *                 ENG-9051 AC5, stated plainly: `other` means THERE IS NOBODY
 *                 TO NOTIFY. It must not be used for a wait on an agent (use
 *                 `agent`) or on a person (use `human`) — doing so parks the
 *                 card on nobody and the blocker is never told.
 *  - `agent`      parked on another AGENT (ENG-9051): a teammate has to act
 *                 before this card can move. Pairs with `waiting_on_agent_id`
 *                 and notifies that agent, so the blocker reaches them instead
 *                 of sitting on a field. Before this existed the only options
 *                 were `external` / `other` (both documented as "nobody to
 *                 notify") or misusing `human`, which notifies the wrong entity.
 *  - `approval`   parked on a HITL approval (ENG-7803): the card auto-parks when
 *                 the agent files a `request_approval`, links to the
 *                 `approval_requests` row (id in `waiting_context`), and
 *                 auto-returns when the approval resolves. A person-court kind
 *                 (the approver is a specific human), so `waiting_on_person_id`
 *                 is meaningful and the card joins the "Awaiting you" queue.
 *
 * `pr-*` are the machine-actionable kinds (pollable, one-click button). `human`
 * / `external` / `other` carry only the ball-in-court semantics (ADR-0044
 * section 5) and keep `waiting_on` as their free text. `other` exists so that
 * requiring a kind (an agent must classify its own waits) never forces a
 * misleading pick - there is always a valid, honest choice.
 */
export type WaitingKind =
  | 'pr-review'
  | 'pr-merged'
  | 'human'
  | 'external'
  | 'other'
  | 'approval'
  | 'agent';

export const WAITING_KINDS: readonly WaitingKind[] = [
  'pr-review',
  'pr-merged',
  'human',
  'external',
  'other',
  'approval',
  'agent',
] as const;

/** True when `pr-*` (the structured, pollable, button-renderable kinds). */
export function isPrWaitingKind(kind: WaitingKind | null | undefined): kind is 'pr-review' | 'pr-merged' {
  return kind === 'pr-review' || kind === 'pr-merged';
}

/**
 * True for a kind that parks the card in a SPECIFIC person's court, and so the
 * kinds for which a structured `waiting_on_person_id` is meaningful.
 *
 * `human` and `approval` (ENG-7803) park on a decision-maker / an approver.
 *
 * ENG-8291: `pr-review` and `pr-merged` now qualify too, and the reasoning that
 * previously excluded them was sound but rested on a premise that turned out to
 * be false everywhere, not just for PRs.
 *
 * ENG-7745 refused a person on a `pr-*` wait because "a person there would
 * promise an 'Awaiting you' entry and a notification we never honestly send".
 * The promise was the problem, not the PR. But the "Awaiting you" queue was
 * never built for ANY kind — `waiting_on_person_id` was written by the API,
 * cleared on un-park, and read by nothing in the tree — so `human` waits were
 * equally invisible. The guard was protecting a promise that was already hollow.
 *
 * A PR awaiting review or a merge tap is one of the most common things an agent
 * is genuinely blocked on by one identifiable person. Excluding it meant the
 * largest category of human-blocking work could not name its human, and the
 * agent's only recourse was prose. (Measured: one PR sat 25.5h on a single merge
 * tap while the owning agent wrote "still waiting on you" into hourly reports.)
 *
 * Still deliberately NOT "every kind":
 *   - `external` is by definition parked on a third party / system with no human
 *     to nudge;
 *   - `other` is the honest catch-all for an unclassified wait, so it carries no
 *     ball-in-court claim either.
 * Both keep their free-text `waiting_on` and stay out of the "Awaiting you"
 * queue, which must only ever list waits a person can actually act on.
 *
 * Shared so the API write path, the notify path, and the console "Awaiting you"
 * view all agree on what "parked on a person" means (the same reason
 * `isPrWaitingKind` is shared). NOTE: `packages/mcp` cannot import this — it is
 * published standalone — so it carries a mirrored copy that
 * `mcp-person-waiting-kind-parity` pins against this one.
 */
export function isPersonWaitingKind(
  kind: WaitingKind | null | undefined,
): kind is 'human' | 'approval' | 'pr-review' | 'pr-merged' {
  return kind === 'human' || kind === 'approval' || isPrWaitingKind(kind);
}

/**
 * ENG-9051: true for a wait parked on another AGENT.
 *
 * Kept separate from `isPersonWaitingKind` rather than folded into it, because
 * the two answer different questions and feed different consumers. "Is there a
 * person to notify and does this belong in someone's 'Awaiting you' queue" is
 * not the same as "is there a teammate agent holding this up". An agent has no
 * `person_id`, so widening the person predicate would put `waiting_on_person_id`
 * in scope for a kind that can never populate it — and would quietly add agent
 * blockers to a human queue that must only list waits a person can act on.
 */
export function isAgentWaitingKind(kind: WaitingKind | null | undefined): kind is 'agent' {
  return kind === 'agent';
}

/**
 * ENG-9051: true when the wait names a specific actor who can be TOLD about it —
 * a person or an agent. The union of the two predicates above.
 *
 * This is the honest definition of `other`'s complement, and it is what AC5 of
 * ENG-9051 is about: `external` and `other` are the kinds with nobody to notify,
 * and they are the only two. A caller that wants "should something be notified"
 * should ask this rather than `isPersonWaitingKind`, which now answers a
 * narrower question than its name once implied.
 */
export function isNotifiableWaitingKind(kind: WaitingKind | null | undefined): boolean {
  return isPersonWaitingKind(kind) || isAgentWaitingKind(kind);
}

/**
 * ENG-7803: true for the HITL-approval wait kind. Its card is server-managed
 * (auto-parked on `request_approval`, auto-returned on resolution), linked to an
 * `approval_requests` row via `waiting_context.approval_request_id`.
 */
export function isApprovalWaitingKind(kind: WaitingKind | null | undefined): kind is 'approval' {
  return kind === 'approval';
}

/**
 * ENG-7803: the `approval_requests.id` a `waiting_kind='approval'` card is
 * parked on, read back from the context bag. Null for any non-approval or
 * unlinked card. The approval-side `approval_requests.kanban_item_id` column is
 * the authoritative approval->card link; this is the card->approval reverse the
 * reaper + console read.
 */
export function approvalRequestIdFromContext(ctx: WaitingContext | null | undefined): string | null {
  const v = ctx?.['approval_request_id'];
  // Trim so a whitespace-only id (malformed context) resolves to null rather than
  // a "no such approval" lookup that would spuriously auto-return the card.
  const id = typeof v === 'string' ? v.trim() : '';
  return id.length > 0 ? id : null;
}

const WAITING_KIND_SET: ReadonlySet<string> = new Set<string>(WAITING_KINDS);

/** Narrowing guard for an arbitrary value against the canonical kind set. */
export function isWaitingKind(value: unknown): value is WaitingKind {
  return typeof value === 'string' && WAITING_KIND_SET.has(value);
}

/**
 * One agent-offered response option on a `human`-court waiting card - the board
 * -native analog of a `request_buttons` option. The human clicks one to unblock
 * the card. `label` is shown; `value` is the machine answer handed back to the
 * agent (defaults to `label` when the agent only gives a label).
 */
export interface WaitingChoice {
  label: string;
  value: string;
}

/** Max choices an agent may offer on a card (keeps the button row sane). */
export const MAX_WAITING_CHOICES = 5;
const MAX_WAITING_CHOICE_LABEL = 75; // matches the request_buttons label cap
const MAX_WAITING_CHOICE_VALUE = 200;

/**
 * ENG-9330: max length of the persisted `system` label. Matches the console
 * pill's own truncation (`waiting-blocker.ts` / `WaitingBadge`), by CODE
 * POINT rather than byte length — `Array.from(str).length` counts a surrogate
 * pair (most emoji) as one character, where `.length` would count two and
 * split it mid-codepoint. A short display label is exactly the case where an
 * agent is likely to reach for one.
 */
export const MAX_WAITING_SYSTEM = 32;

/**
 * The per-kind param bag persisted in `agent_kanban_items.waiting_context`
 * (jsonb). Forward-compatible: a future kind can add its own keys without a
 * migration. `pr-*` kinds populate `repo` + `pr_number`; a `human`-court wait may
 * populate `choices` (buttons the human picks from).
 */
export interface WaitingContext {
  /** "owner/name", e.g. "Integrity-Labs/augmented" (pr-* kinds). */
  repo?: string;
  /** The pull-request number (pr-* kinds). */
  pr_number?: number;
  /** Agent-offered response buttons (human-court waits). */
  choices?: WaitingChoice[];
  /**
   * ENG-7676 (ADR-0044): the FULL decision context the human needs to answer
   * from the card alone - the actual questions, options, links, specifics.
   * `waiting_on` stays a one-line headline; this is the body. Human-court waits
   * (human / external / other); cleared with the wait like the rest of the bag.
   */
  details?: string;
  /**
   * ENG-7803 (approval kind): the `approval_requests.id` this card is parked on.
   * The card->approval reverse link the reaper + console read; the authoritative
   * approval->card link is `approval_requests.kanban_item_id`.
   */
  approval_request_id?: string;
  /**
   * ENG-9330: an explicit name for the non-human thing a card is parked on
   * ("CodeRabbit", "the deploy pipeline"). SHORT — a display label for a pill,
   * not prose; see `MAX_WAITING_SYSTEM`. Read by `resolveWaitingSystemName`
   * (webapp `lib/waiting-blocker.ts`), which checks this FIRST, ahead of the
   * "PR checks" derivation for a personless `pr-*` wait — an agent that knows
   * the actual vendor should never be overridden by a generic guess. Valid on
   * every `waiting_kind`, unlike `waiting_on_person`/`waiting_on_agent`: the
   * system a card is blocked on is orthogonal to WHO (if anyone) also has to
   * act, so this is never rejected for the kind, only for content.
   */
  system?: string;
  /** Forward-compatible: kinds may add their own params. */
  [key: string]: unknown;
}

/** Max length of the persisted `details` body (keeps a card row bounded). */
export const MAX_WAITING_DETAILS = 4000;

/**
 * Normalise an arbitrary `choices` value into a clean `WaitingChoice[]`. Accepts
 * either bare strings (`"Ship it"` -> `{label:"Ship it", value:"Ship it"}`) or
 * `{label, value}` objects; trims, drops blanks, dedupes by value, caps the
 * count and per-field length. Returns undefined when nothing usable remains so
 * callers persist a clean absence rather than `[]`.
 */
export function normalizeWaitingChoices(raw: unknown): WaitingChoice[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: WaitingChoice[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (out.length >= MAX_WAITING_CHOICES) break;
    let label = '';
    let value = '';
    if (typeof item === 'string') {
      label = item.trim();
      value = label;
    } else if (item && typeof item === 'object') {
      const rec = item as Record<string, unknown>;
      label = typeof rec.label === 'string' ? rec.label.trim() : '';
      const rawValue = rec.value;
      value = typeof rawValue === 'string' && rawValue.trim().length > 0 ? rawValue.trim() : label;
    }
    if (!label || !value) continue;
    if (seen.has(value)) continue; // dedupe by machine value (matches request_buttons)
    seen.add(value);
    out.push({
      label: label.slice(0, MAX_WAITING_CHOICE_LABEL),
      value: value.slice(0, MAX_WAITING_CHOICE_VALUE),
    });
  }
  return out.length > 0 ? out : undefined;
}

/** A derived action button/link for a waiting card. */
export interface WaitingAction {
  label: string;
  /** Absolute URL, or null when the kind has no derivable link. */
  url: string | null;
}

/**
 * Normalise a raw jsonb `waiting_context` into a typed `WaitingContext`,
 * coercing `pr_number` (which can arrive as a string from a form) and dropping
 * blanks. Returns null when nothing usable remains, so callers can persist a
 * clean null rather than `{}`.
 */
export function normalizeWaitingContext(raw: unknown): WaitingContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;
  const out: WaitingContext = {};

  if (typeof src.repo === 'string') {
    const repo = src.repo.trim();
    if (repo.length > 0) out.repo = repo;
  }

  if (src.pr_number != null) {
    const n = typeof src.pr_number === 'string' ? Number(src.pr_number) : src.pr_number;
    if (typeof n === 'number' && Number.isInteger(n) && n > 0) out.pr_number = n;
  }

  if (src.choices !== undefined) {
    const choices = normalizeWaitingChoices(src.choices);
    if (choices) out.choices = choices;
  }

  // ENG-7676: the full decision body. Trim, drop blank, cap length so a card
  // row stays bounded (the human-facing render un-clamps it).
  if (typeof src.details === 'string') {
    const details = src.details.trim();
    if (details.length > 0) out.details = details.slice(0, MAX_WAITING_DETAILS);
  }

  // ENG-9330: the explicit system label. Trimmed, blank-dropped, and capped by
  // CODE POINT (Array.from, not .slice) so the cut cannot land mid-surrogate-
  // pair and render a replacement glyph — `MAX_WAITING_SYSTEM`'s own doc names
  // this as the same defect `trimmedLabel` in kanban-columns.ts was fixed for
  // (CodeRabbit caught it there first). Capping here, not just on display, is
  // what makes the write path honest about what it accepts rather than storing
  // an unbounded string the console silently clips.
  if (typeof src.system === 'string') {
    const system = src.system.trim();
    if (system.length > 0) {
      const chars = Array.from(system);
      out.system = chars.length > MAX_WAITING_SYSTEM ? chars.slice(0, MAX_WAITING_SYSTEM).join('') : system;
    }
  }

  // Preserve any forward-compatible keys we don't model explicitly.
  for (const [k, v] of Object.entries(src)) {
    if (k === 'repo' || k === 'pr_number' || k === 'choices' || k === 'details' || k === 'system') continue;
    if (v !== undefined && v !== null) out[k] = v;
  }

  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Derive the action button for a waiting card from its kind + context. Only the
 * `pr-*` kinds yield a button (the single source of truth is `{repo, pr_number}`
 * - the URL is never stored). `human` / `external` return null; their card
 * renders the free-text `waiting_on` instead. Returns null when a `pr-*` kind is
 * missing the repo or PR number (a half-filled form), so the caller falls back
 * to `waiting_on`.
 */
export function deriveWaitingAction(
  kind: WaitingKind | null | undefined,
  context: WaitingContext | null | undefined,
): WaitingAction | null {
  if (!isPrWaitingKind(kind)) return null;
  const repo = typeof context?.repo === 'string' ? context.repo.trim() : '';
  const pr = context?.pr_number;
  if (!repo || typeof pr !== 'number' || !Number.isInteger(pr) || pr <= 0) return null;
  const verb = kind === 'pr-merged' ? 'Merge' : 'Review';
  return { label: `${verb} PR #${pr}`, url: `https://github.com/${repo}/pull/${pr}` };
}

/**
 * ENG-7673 / ENG-7706: default operator affordances for a pr-* wait when the
 * agent supplied no explicit `waiting_context.choices`. "Merge on green" etc.
 * do NOT merge anything themselves - the click travels back on the direct-chat
 * rail as a waiting_response and tells the AGENT what the operator decided.
 * Single source of truth for BOTH renderers (the Slack review card and the
 * console Respond panel), so the affordances never drift apart.
 */
/**
 * `left-feedback` exists because "Needs changes" was carrying two different
 * meanings and the agent could not tell them apart.
 *
 * Reported by Brad, 2026-08-10: *"When I click on Needs Change it's usually due
 * to feedback on the PR."* So the common case was a reviewer who had already
 * said everything on the PR itself, and the tap was meant as "go read it" — but
 * what reached the agent was a bare verdict with no pointer, and the agent's
 * rational next move was to ask the reviewer what they meant. That round trip is
 * the whole cost: the answer already existed, in the place the agent did not
 * think to look.
 *
 * The two are deliberately kept as separate buttons rather than one relabelled
 * button, because they ask for different things:
 *
 *   * `left-feedback`  — "my reasoning is in the PR". The agent should go read
 *     the review comments and work from them. No reply to the reviewer needed.
 *   * `needs-changes`  — "not ready", with no comments written. The agent still
 *     needs to find out why, and asking is correct here.
 *
 * Nothing branches on these values in code: they travel back to the agent as a
 * waiting_response and are read as text. So the LABEL is the interface, and it
 * has to be unambiguous to a human at a glance and to an agent reading it back.
 */
export const PR_REVIEW_DEFAULT_CHOICES: readonly WaitingChoice[] = [
  { label: 'Merge on green', value: 'merge-on-green' },
  // Ordered before "Needs changes" on purpose: it is the case Brad reports as
  // the usual one, so it should be the button that falls under the thumb first.
  { label: 'Left feedback on the PR', value: 'left-feedback' },
  { label: 'Needs changes', value: 'needs-changes' },
  { label: 'Hold', value: 'hold' },
];

/** Default operator affordances for a `pr-merged` wait (see above). */
export const PR_MERGED_DEFAULT_CHOICES: readonly WaitingChoice[] = [
  { label: 'Merged', value: 'merged' },
  { label: 'Hold', value: 'hold' },
  { label: 'Abandon', value: 'abandon' },
];

/**
 * The per-kind default choices for a pr-* wait; empty for non-pr kinds (their
 * affordances are always agent-supplied).
 */
export function defaultPrWaitingChoices(kind: WaitingKind | null | undefined): WaitingChoice[] {
  if (kind === 'pr-review') return [...PR_REVIEW_DEFAULT_CHOICES];
  if (kind === 'pr-merged') return [...PR_MERGED_DEFAULT_CHOICES];
  return [];
}

/**
 * ENG-10336 — WHO sent a card to `needs_attention`, and therefore what it means.
 *
 * Two sweeps write that one status and they mean OPPOSITE things:
 *
 *   - `reapStaleKanbanItems`  — an `in_progress` lease expired. The AGENT
 *     stopped. This is the dead-letter the status was built for, and the
 *     console's "This task stalled and needs a person" is true of it.
 *   - `reapStaleWaitingItems` — a card sat in `waiting` past the 7-day
 *     threshold. The agent is perfectly healthy; a HUMAN has owed an answer for
 *     a week. Nothing stalled, and telling the reader it did is false.
 *
 * Nothing downstream told them apart, so a board full of unanswered questions to
 * one person rendered as "nobody is driving this" — which is how ten of them sat
 * for a month. The discriminator was already on the row; it just had no name.
 *
 * Exported as constants for the same reason `UNPARKED_CARD_STATUS` is: the
 * writer (the reaper cron) and the reader (the console panel) live in different
 * packages, and a literal in two places is how they drift.
 */
export const NEEDS_ATTENTION_ACTORS = {
  /** `reapStaleKanbanItems` — an in_progress lease expired. */
  agentStalled: 'system:kanban-stale-reaper',
  /** host-runtime's watchdog — same class: a card nobody judged. */
  agentStalledWatchdog: 'system:kanban-stale-watchdog',
  /** `reapStaleWaitingItems` — a human has owed an answer for 7+ days. */
  humanOwed: 'system:kanban-waiting-reaper',
} as const;

/**
 * Which population a `needs_attention` card belongs to.
 *
 * `unknown` is a first-class answer, not a failure. A card can reach this status
 * by routes that are neither reaper — a human moving it by hand, an older row
 * written before `last_actor_id` was stamped, a future writer nobody has taught
 * this function about. Guessing one of the two specific framings for those would
 * reintroduce the defect this exists to fix, in the other direction: asserting a
 * cause nobody observed. Callers must render `unknown` as the neutral copy.
 */
export type NeedsAttentionOrigin = 'human-owed' | 'agent-stalled' | 'unknown';

export function needsAttentionOrigin(
  lastActorId: string | null | undefined,
): NeedsAttentionOrigin {
  if (lastActorId === NEEDS_ATTENTION_ACTORS.humanOwed) return 'human-owed';
  if (
    lastActorId === NEEDS_ATTENTION_ACTORS.agentStalled ||
    lastActorId === NEEDS_ATTENTION_ACTORS.agentStalledWatchdog
  ) {
    return 'agent-stalled';
  }
  return 'unknown';
}

/**
 * ENG-10336 — total time this card has spent in a human's court, across EVERY
 * park/un-park cycle, in seconds. `null` when nothing is known.
 *
 * WHY THIS HAD TO EXIST BEFORE RE-PRESENTING AN ASK WAS USABLE. Re-parking a
 * reaped card directly (`needs_attention -> waiting`) does not work: the
 * ENG-8639 trigger treats both statuses as "in the human's court" and PINS
 * `waiting_since` to the original park, so the card re-enters `waiting` already
 * past the 7-day cutoff and the reaper takes it again within seconds (measured:
 * 14s). The route that DOES work is `needs_attention -> in_progress -> waiting`,
 * because the trigger banks the elapsed time into `waiting_seconds_total` and
 * re-stamps `waiting_since` on the way back in.
 *
 * But that route resets the VISIBLE age to zero. So before this function, the
 * only way to re-ask an unanswered question was to destroy the evidence of how
 * long it had gone unanswered. Reading the banked total is what makes
 * re-presenting stop costing the truth.
 *
 * REJECTED, and recorded here because the next reader will reach for it exactly
 * as the last one did: re-stamping `waiting_since` on a
 * `needs_attention -> waiting` move. It fails twice. It destroys the honest
 * "blocked N days" age the column exists to provide — precisely the ENG-8660
 * regression, where `updated_at` let any unrelated write hide a card from the
 * backstop for another week. And it lets a genuinely dead card cycle
 * `waiting -> needs_attention -> waiting` forever, which is the one thing the
 * reaper exists to prevent. The clock is pinned ON PURPOSE; the fix was never to
 * unpin it.
 *
 * `banked` alone is returned once the card resolves (`waiting_since` cleared by
 * the trigger). The open span is added while it is still parked. Both are
 * clamped at zero: a backfilled or restored `waiting_since` can sit in the
 * future under clock skew, and a negative contribution would silently subtract
 * from a real total — the same defence the trigger's own GREATEST(...,0) makes.
 */
export function cumulativeWaitingSeconds(
  waitingSecondsTotal: number | null | undefined,
  waitingSince: string | null | undefined,
  nowMs: number,
): number | null {
  const banked =
    typeof waitingSecondsTotal === 'number' && Number.isFinite(waitingSecondsTotal)
      ? Math.max(0, waitingSecondsTotal)
      : 0;

  let open = 0;
  // Whether we actually MEASURED an open span, which is not the same as having
  // been handed a `waitingSince` (CodeRabbit, #5945). A non-empty but
  // unparseable timestamp with nothing banked used to fall through to `0`, and
  // the panel renders every non-null result as a duration — so a row with a
  // corrupt timestamp claimed "0s" of waiting. A confident zero for something
  // unknown is the exact failure the null case exists to prevent, so the
  // decision has to key on the parse, not on the presence of the string.
  let measuredOpenSpan = false;
  if (waitingSince) {
    const startedMs = Date.parse(waitingSince);
    if (Number.isFinite(startedMs)) {
      measuredOpenSpan = true;
      open = Math.max(0, Math.floor((nowMs - startedMs) / 1000));
    }
  }

  // Nothing banked and no open span we could measure: we do not know, and the
  // caller must omit the figure rather than render a confident zero.
  if (banked === 0 && !measuredOpenSpan) return null;
  return banked + open;
}
